import React, { FunctionComponent, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router'
import { reduxForm, InjectedFormProps, SubmissionError } from 'redux-form'

import useDispatch from 'hooks/useDispatch'
import useReduxSelector from 'hooks/useReduxSelector'

import { ParagraphXs } from 'components/display/Typography'
import { fetchVoucher, payVoucher } from '../../../actions/voucherActions'
import { mediaMax } from '../../../styles/media'
import { formatPrice } from '../../../components/utils/pricing'

import SelectPaymentMethod, {
  SelectPaymentMethodFormValues,
} from '../../user/PaymentMethods/SelectPaymentMethod'
import { handlePaymentMethod } from '../../user/PaymentMethods/handlePaymentMethod'
import LazyStripeElements from '../../../components/stripe/LazyStripe'

import Button from '../../../components/display/Button'
import Container from '../../../components/layout/Container'
import InlineSpinner from '../../../components/display/InlineSpinner'
import Overlay from '../../../components/display/Overlay'
import Spinner from '../../../components/display/Spinner'

import Voucher from '../Voucher'

const SubmitButton = styled(Button)`
  min-width: 160px;
  ${mediaMax.md`
    width: 100%;
  `}
`

const Wrapper = styled.form`
  position: relative;
  padding: 40px 0;
`

const VoucherBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.textGreyLight};
  border-radius: 6px;
  padding: 32px 24px;
  margin-bottom: 30px;

  .voucher {
    width: 100%;
    max-width: 340px;
    margin: 0 auto;
    margin-bottom: 40px;
  }

  h2 {
    margin: 0;
    margin-bottom: 32px;
    line-height: 1;

    text-align: center;
    font-size: 20px;
    font-weight: normal;
  }
`

const VoucherDetails = styled.div`
  > div {
    margin-bottom: 1em;
  }
`

export type VoucherPayProps = InjectedFormProps<
  SelectPaymentMethodFormValues
> & {
  error?: SubmissionError
}

const VoucherPay: FunctionComponent<VoucherPayProps> = ({
  handleSubmit,
  submitting,
  error,
}) => {
  const dispatch = useDispatch()
  const paymentMethodRef = useRef(null)
  const { voucherId } = useParams<{ voucherId: string }>()

  const vouchers = useReduxSelector((state) => state?.vouchers)

  useEffect(() => {
    if (voucherId) {
      // @ts-ignore -- fetchVoucher not typed yet
      dispatch(fetchVoucher(voucherId))
      window.scrollTo(0, 0)
    }
  }, [])

  const suggestable = vouchers.data && vouchers.data[vouchers.data.suggestable]
  const { paying, fetching } = vouchers

  return (
    <LazyStripeElements>
      <Wrapper
        className="qa-payment"
        onSubmit={handleSubmit((formValues) =>
          handlePaymentMethod(paymentMethodRef).then((credit_card_id) =>
            // @ts-ignore -- payVoucher not typed yet
            dispatch(payVoucher(voucherId, { ...formValues, credit_card_id }))
          )
        )}
      >
        <Container>
          <div className="row">
            <div className="col-xs-12 col-md-4 col-md-push-8">
              <VoucherBox className="qa-voucher-details">
                <h2>Gift Voucher</h2>
                {/* @ts-ignore -- Voucher not typed yet */}
                <Voucher
                  title={suggestable?.title}
                  backgroundImage={suggestable?.promo_image.large}
                />
                {vouchers.data && (
                  <VoucherDetails>
                    <div>
                      Amount: {vouchers.data.currency}{' '}
                      {formatPrice(vouchers.data.amount)}
                    </div>
                    <div>To: {vouchers.data.recipient_name}</div>
                    <div>From: {vouchers.data.from_name}</div>
                    <div>Message:</div>
                    <p>{vouchers.data.message}</p>
                  </VoucherDetails>
                )}
              </VoucherBox>
            </div>

            <div className="col-xs-12 col-md-6 col-md-pull-4">
              <SelectPaymentMethod
                ref={paymentMethodRef}
                error={error}
                rounded
              />

              <ParagraphXs styleLink>
                By purchasing a voucher, you&apos;re agreeing to the{' '}
                <a href="/terms" target="_blank">
                  Riparide Terms & Conditions
                </a>
                .
              </ParagraphXs>

              <SubmitButton
                type="submit"
                disabled={fetching || submitting || paying}
                rounded
              >
                {paying || submitting ? <InlineSpinner /> : 'Buy Voucher'}
              </SubmitButton>
            </div>
          </div>

          {submitting && (
            <Overlay>
              <Spinner />
            </Overlay>
          )}
        </Container>
      </Wrapper>
    </LazyStripeElements>
  )
}

const VoucherPayForm = reduxForm({
  form: 'VoucherPay',
})(VoucherPay)

export default VoucherPayForm
